import type { FC } from 'react';
import { useCallback } from 'react';
import { Form, Input, Modal, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { useAPI } from 'src/util/use-api';
import { useBinderContext } from 'src/util/context/BinderContext';
import { QUERIES } from 'src/util/globals';
import { useAddToBinder } from 'src/hooks/useBinders';

type FormValues = {
  name: string;
};

export const CreateBinder: FC = () => {
  const { createBinderModalVisible, toggleCreateBinderModal, setBinderId } =
    useBinderContext();
  const { t } = useTranslation('binders');
  const [form] = Form.useForm<FormValues>();
  const client = useQueryClient();
  const router = useRouter();
  const { API } = useAPI();

  const { mutateAsync: createBinder, isPending: binderCreationProgress } =
    useMutation({
      mutationFn: async (name: string) => {
        return (
          await API?.bindersAPI.bindersCreate({
            binderRequest: {
              name,
            },
          })
        )?.data;
      },

      onSuccess: async (binder) => {
        await client.invalidateQueries({
          queryKey: [QUERIES.BINDERS],
        });

        toggleCreateBinderModal();
        if (binder) {
          setBinderId(binder.external_id);
          // redirect to the binder
          router.push(`/binders/${binder.external_id}`);
        }
        notification.success({
          message: t('Binder has been created'),
        });
      },
    });

  const handleSubmit = useCallback(async () => {
    const values = await form.validateFields();

    await createBinder(values.name);
  }, [createBinder, form]);

  return (
    <Modal
      open={createBinderModalVisible}
      onCancel={() => toggleCreateBinderModal()}
      destroyOnClose
      afterClose={() => form.resetFields()}
      onOk={handleSubmit}
      okButtonProps={{ loading: binderCreationProgress }}
      okText={t('Create')}
      title={t(`Create new binder`)}
    >
      <Form form={form} layout="vertical" autoComplete="off">
        <Form.Item
          name="name"
          label={t('Binder name')}
          rules={[{ required: true }]}
        >
          <Input size="large" placeholder="Name" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
